import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEvents } from '@/lib/events-api';
import {
  listEventSubmissions, fetchSubmissionThumbnailDataUrl,
  reviewSubmission, bulkReviewSubmissions, softDeleteSubmission,
} from '@/lib/submissions-api';
import {
  Heart, BookOpen, Tv, Archive, RotateCcw, Eye, Trash2,
  Image as ImageIcon, Film, ArrowDown, ArrowUp, Check, Square, CheckSquare,
} from 'lucide-react';

const STATUS_CHIPS = [
  { key: 'all', label: 'All' }, { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' }, { key: 'favorites', label: 'Favorites' },
  { key: 'lookbook', label: 'Lookbook' }, { key: 'frame', label: 'Frame' },
  { key: 'archived', label: 'Archived' }, { key: 'deleted', label: 'Deleted' },
];
const PAGE_SIZE = 24;

export default function HostVaultPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const passedEventId = sp.get('event_id');

  const [hostedEvents, setHostedEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [manualEventId, setManualEventId] = useState(null);
  const [vaultData, setVaultData] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [mediaKind, setMediaKind] = useState(null);
  const [contributorId, setContributorId] = useState(null);
  const [sortDir, setSortDir] = useState('uploaded_recent');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(null);

  const submissions = vaultData?.submissions || [];
  const counts = vaultData?.filter_counts || {};
  const contributors = vaultData?.contributors || [];

  const load = useCallback(async (forcedId, opts) => {
    setLoading(true); setError(null);
    try {
      const list = await listEvents();
      setHostedEvents(list.hosted || []);
      const wanted = forcedId || passedEventId;
      const sorted = [...(list.hosted || [])].sort(
        (a, b) => (b.submission_stats?.total || 0) - (a.submission_stats?.total || 0),
      );
      const chosen =
        (wanted && (list.hosted || []).find(e => e.id === wanted)) ||
        sorted[0] ||
        (list.hosted || []).find(x => x.status === 'COLLECTING') ||
        (list.hosted || [])[0] ||
        null;
      if (!chosen) { setEvent(null); setVaultData(null); return; }
      setEvent(chosen);
      const page = await listEventSubmissions(chosen.id, {
        limit: PAGE_SIZE,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        media_kind: mediaKind || undefined,
        contributor_id: contributorId || undefined,
        sort: sortDir,
      });
      setVaultData(page);
      if (!opts?.skipListReset) setSelected(new Set());
    } catch (e) { setError(e?.message || "Couldn't load the vault."); }
    finally { setLoading(false); }
  }, [passedEventId, statusFilter, mediaKind, contributorId, sortDir]);

  useEffect(() => { if (ready) load(manualEventId || undefined); }, [ready, load, manualEventId]);

  // Fetch thumbnails
  useEffect(() => {
    let cancelled = false;
    const photos = submissions.filter(s => s.media_kind === 'photo');
    (async () => {
      for (const s of photos) {
        if (cancelled) return;
        if (thumbs[s.id] !== undefined) continue;
        const url = await fetchSubmissionThumbnailDataUrl(s.id);
        if (cancelled) return;
        setThumbs(prev => ({ ...prev, [s.id]: url }));
      }
    })();
    return () => { cancelled = true; };
  }, [submissions]); // eslint-disable-line

  async function loadMore() {
    if (!event || !vaultData?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listEventSubmissions(event.id, {
        limit: PAGE_SIZE,
        cursor: vaultData.next_cursor,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        media_kind: mediaKind || undefined,
        contributor_id: contributorId || undefined,
        sort: sortDir,
      });
      setVaultData(prev => prev ? { ...page, submissions: [...prev.submissions, ...page.submissions] } : page);
    } finally { setLoadingMore(false); }
  }

  function toggleSelection(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function onCellTap(sub) {
    if (selectionMode) { toggleSelection(sub.id); return; }
    if (!event) return;
    navigate(`/host-review?event_id=${event.id}&focus_id=${sub.id}&status=${statusFilter}`);
  }

  async function onTapHeart(e, sub) {
    e.stopPropagation();
    setBusy(sub.id);
    try { await reviewSubmission(sub.id, { favorite: !sub.favorited }); await load(event?.id, { skipListReset: true }); }
    catch (err) { setError(err?.message || "Couldn't update favorite."); }
    finally { setBusy(null); }
  }

  async function onBulk(action) {
    if (!event || selected.size === 0) return;
    const ids = Array.from(selected);
    const label =
      action === 'archive' ? `Archive ${ids.length} view(s)?`
      : action === 'unarchive' ? `Unarchive ${ids.length} view(s)?`
      : action === 'favorite' ? `Favorite ${ids.length} view(s)?`
      : action === 'lookbook' ? `Add ${ids.length} to Lookbook?`
      : `Delete ${ids.length} view(s)? You can restore for 30 days.`;
    if (!window.confirm(label)) return;
    setBusy('bulk');
    try {
      if (action === 'delete') {
        await Promise.all(ids.map(id => softDeleteSubmission(id)));
      } else {
        const actionsBody =
          action === 'archive' ? { archive: true }
          : action === 'unarchive' ? { archive: false }
          : action === 'favorite' ? { favorite: true }
          : { lookbook: true };
        await bulkReviewSubmissions(event.id, ids, actionsBody);
      }
      setSelectionMode(false); setSelected(new Set());
      await load(event.id);
    } catch (err) { setError(err?.message || 'Bulk action failed.'); }
    finally { setBusy(null); }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 relative" data-testid="host-vault-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-extrabold text-[#0B1B46]" data-testid="host-vault-title">View Vault</h1>
        <button onClick={() => { setSelectionMode(!selectionMode); setSelected(new Set()); }}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E3E8EF] rounded-full text-xs font-bold text-[#146BFF] bg-white hover:bg-[#EFF5FF] transition-colors"
          data-testid="host-vault-select-toggle">
          {selectionMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
          {selectionMode ? 'Done' : 'Select'}
        </button>
      </div>
      <p className="text-sm text-[#5B6472] mb-4">
        Every private submission your attendees have delivered. Only you can see this list.
      </p>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="host-vault-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !event ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="host-vault-no-event">
          <p className="font-bold text-[#0B1B46]">No hosted events yet</p>
          <p className="text-sm text-[#5B6472] mt-1">Create an event to start collecting private submissions.</p>
          <button onClick={() => navigate('/create-event')} className="h-10 px-6 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg" data-testid="host-vault-create-btn">Create Event</button>
        </div>
      ) : (
        <>
          {/* Event card */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-3" data-testid="host-vault-event-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-1">{event.name}</p>
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-0.5 text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">{event.status}</span>
              {event.current_event_id && <span className="px-2 py-0.5 text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">#{event.current_event_id}</span>}
            </div>
          </div>

          {/* Event switcher */}
          {hostedEvents.length > 1 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-3" data-testid="host-vault-event-switcher">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Switch event</p>
              {hostedEvents.map(e => {
                const isActive = e.id === event.id;
                return (
                  <button key={e.id} onClick={() => setManualEventId(e.id)}
                    className={`flex items-center justify-between w-full p-2.5 rounded-lg mb-1 text-left transition-colors ${isActive ? 'bg-[#EFF5FF]' : 'bg-[#F8FAFC] hover:bg-[#F3F5F8]'}`}
                    data-testid={`host-vault-switch-${e.id}`}>
                    <span className={`text-sm truncate flex-1 ${isActive ? 'text-[#146BFF] font-bold' : 'text-[#0B1B46]'}`}>{e.name}</span>
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full ml-2">{e.submission_stats?.delivered || 0} delivered</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Status chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="host-vault-status-chips">
            {STATUS_CHIPS.map(c => {
              const active = statusFilter === c.key;
              const count = counts[c.key];
              return (
                <button key={c.key} onClick={() => setStatusFilter(c.key)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-colors ${active ? 'border-[#146BFF] bg-[#EFF5FF] text-[#146BFF]' : 'border-[#E3E8EF] bg-white text-[#5B6472] hover:bg-[#F3F5F8]'}`}
                  data-testid={`host-vault-chip-${c.key}`}>
                  {c.label}
                  {count !== undefined && <span className={active ? 'text-[#146BFF]' : 'opacity-60'}>{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Media kind + sort row */}
          <div className="flex items-center gap-1.5 mb-3">
            <button onClick={() => setMediaKind(mediaKind === 'photo' ? null : 'photo')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${mediaKind === 'photo' ? 'border-[#146BFF] bg-[#EFF5FF] text-[#146BFF]' : 'border-[#E3E8EF] bg-white text-[#5B6472]'}`}
              data-testid="host-vault-media-photos">Photos</button>
            <button onClick={() => setMediaKind(mediaKind === 'video' ? null : 'video')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${mediaKind === 'video' ? 'border-[#146BFF] bg-[#EFF5FF] text-[#146BFF]' : 'border-[#E3E8EF] bg-white text-[#5B6472]'}`}
              data-testid="host-vault-media-videos">Videos</button>
            <div className="flex-1" />
            <button onClick={() => setSortDir(s => s === 'uploaded_recent' ? 'uploaded_oldest' : 'uploaded_recent')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E3E8EF] bg-white text-[#5B6472] hover:bg-[#F3F5F8] transition-colors"
              data-testid="host-vault-sort-btn">
              {sortDir === 'uploaded_recent' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
              {sortDir === 'uploaded_recent' ? 'Most Recent' : 'Oldest First'}
            </button>
          </div>

          {/* Contributor chips */}
          {contributors.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3" data-testid="host-vault-contributor-chips">
              <button onClick={() => setContributorId(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border whitespace-nowrap transition-colors ${!contributorId ? 'border-[#146BFF] bg-[#EFF5FF] text-[#146BFF]' : 'border-[#E3E8EF] bg-white text-[#5B6472]'}`}
                data-testid="host-vault-contributor-all">All</button>
              {contributors.map(c => {
                const active = contributorId === c.contributor_id;
                return (
                  <button key={c.contributor_id} onClick={() => setContributorId(active ? null : c.contributor_id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border whitespace-nowrap transition-colors ${active ? 'border-[#146BFF] bg-[#EFF5FF] text-[#146BFF]' : 'border-[#E3E8EF] bg-white text-[#5B6472]'}`}
                    data-testid={`host-vault-contributor-${c.contributor_id}`}>
                    {c.first_name}
                    <span className={active ? 'text-[#146BFF]' : 'opacity-60'}>{c.count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Grid */}
          {submissions.length === 0 ? (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-6 text-center" data-testid="host-vault-empty">
              <p className="font-bold text-[#0B1B46]">Nothing here yet</p>
              <p className="text-sm text-[#5B6472] mt-1">Adjust the filters above or invite more attendees.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="host-vault-grid">
              {submissions.map(s => {
                const isSelected = selected.has(s.id);
                return (
                  <div key={s.id} onClick={() => onCellTap(s)}
                    className="relative cursor-pointer group rounded-xl overflow-hidden border border-[#E3E8EF] hover:border-[#146BFF] hover:shadow-md transition-all"
                    data-testid={`vault-item-${s.id}`}>
                    <div className="relative w-full aspect-[3/2] bg-[#F3F5F8]">
                      {s.media_kind === 'video' ? (
                        <div className="w-full h-full bg-[#0B1B46] flex items-center justify-center">
                          <Film className="w-7 h-7 text-white" />
                        </div>
                      ) : thumbs[s.id] ? (
                        <img src={thumbs[s.id]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}

                      {/* Contributor chip */}
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 rounded text-[9px] font-bold text-white max-w-[70%] truncate"
                        data-testid={`vault-item-contributor-${s.id}`}>
                        {s.contributor_first_name || 'Attendee'}
                      </div>

                      {/* Video duration */}
                      {s.media_kind === 'video' && s.video_seconds != null && (
                        <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 rounded text-[9px] font-bold text-white">
                          {Math.round(s.video_seconds)}s
                        </div>
                      )}

                      {/* Favorite heart */}
                      <button onClick={e => onTapHeart(e, s)} disabled={busy === s.id}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/35 flex items-center justify-center hover:bg-black/55 transition-colors"
                        data-testid={`vault-item-heart-${s.id}`}>
                        <Heart className={`w-4 h-4 ${s.favorited ? 'fill-red-500 text-red-500' : 'text-white opacity-90'}`} />
                      </button>

                      {/* Selection dot */}
                      {selectionMode && (
                        <div className={`absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-[#146BFF] border-[#146BFF]' : 'bg-black/30 border-white'}`}
                          data-testid={`vault-item-select-${s.id}`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      )}

                      {/* Reviewed tick */}
                      {s.reviewed && !selectionMode && (
                        <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-[#3BA878]/85 flex items-center justify-center"
                          data-testid={`vault-item-reviewed-${s.id}`}>
                          <Eye className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}

                      {/* Archived badge */}
                      {s.archived && (
                        <div className="absolute top-8 left-1.5 px-1.5 py-0.5 bg-[#B86500] rounded text-[9px] font-extrabold text-white"
                          data-testid={`vault-item-archived-${s.id}`}>Archived</div>
                      )}

                      {/* Deleted badge */}
                      {s.deletion_pending && (
                        <div className="absolute top-8 left-1.5 px-1.5 py-0.5 bg-[#C93434] rounded text-[9px] font-extrabold text-white"
                          data-testid={`vault-item-deleted-${s.id}`}>Deleted</div>
                      )}
                    </div>

                    {s.caption && (
                      <p className="text-[11px] text-[#0B1B46] px-2 py-1 truncate" data-testid={`vault-item-caption-${s.id}`}>{s.caption}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Load more */}
          {vaultData?.has_more && (
            <div className="flex justify-center mt-4">
              <button onClick={loadMore} disabled={loadingMore}
                className="h-10 px-6 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg text-sm hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
                data-testid="host-vault-load-more-btn">{loadingMore ? 'Loading…' : 'Load More'}</button>
            </div>
          )}

          {error && (
            <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mt-3" data-testid="host-vault-error">{error}</div>
          )}
        </>
      )}

      {/* Sticky bulk action bar */}
      {selectionMode && selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 max-w-lg w-[calc(100%-2rem)] flex items-center gap-2 px-4 py-3 rounded-full bg-[#0B1B46] shadow-xl z-50"
          data-testid="host-vault-bulk-bar">
          <span className="text-white font-extrabold text-sm" data-testid="host-vault-bulk-count">{selected.size} selected</span>
          <div className="flex-1" />
          <button onClick={() => onBulk('favorite')} disabled={!!busy}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
            data-testid="host-vault-bulk-favorite"><Heart className="w-4 h-4 text-[#C93434]" /></button>
          <button onClick={() => onBulk('lookbook')} disabled={!!busy}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
            data-testid="host-vault-bulk-lookbook"><BookOpen className="w-4 h-4 text-[#146BFF]" /></button>
          <button onClick={() => onBulk(statusFilter === 'archived' ? 'unarchive' : 'archive')} disabled={!!busy}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
            data-testid="host-vault-bulk-archive">
            {statusFilter === 'archived'
              ? <RotateCcw className="w-4 h-4 text-[#B86500]" />
              : <Archive className="w-4 h-4 text-[#B86500]" />}
          </button>
          <button onClick={() => onBulk('delete')} disabled={!!busy}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-[#FEECEC] disabled:opacity-50 transition-colors"
            data-testid="host-vault-bulk-delete"><Trash2 className="w-4 h-4 text-[#C93434]" /></button>
        </div>
      )}
    </div>
  );
}
